import { useState } from "react";

/**
 * Custom hook for clipboard operations with fallbacks
 * @returns {Object} Clipboard methods and state
 */
const useClipboard = () => {
  const [copyStatus, setCopyStatus] = useState({
    copied: false,
    error: null,
  });

  /**
   * Extract URL parameter value from a URL string
   * @param {string} url - Full URL string
   * @param {string} paramName - Parameter name to extract
   * @returns {string|null} - Extracted parameter value or null if not found
   */
  const extractUrlParam = (url, paramName) => {
    try {
      // First, try standard URL parsing
      const parsedUrl = new URL(url);
      const paramValue = parsedUrl.searchParams.get(paramName);

      if (paramValue) {
        return paramValue;
      }

      // If not found, check if the parameter might be after a hash fragment
      // This handles cases like: example.com/#data=abc?shareUrl=value
      if (parsedUrl.hash) {
        // Look for ?paramName= or &paramName= in the hash
        const hashContent = parsedUrl.hash.substring(1); // Remove the # character

        // Try to find the parameter in the hash content
        const paramRegex = new RegExp(`[?&]${paramName}=([^&#]*)`, "i");
        const hashMatch = hashContent.match(paramRegex);

        if (hashMatch && hashMatch[1]) {
          return decodeURIComponent(hashMatch[1]);
        }
      }

      return null;
    } catch (err) {
      console.error("Failed to parse URL:", err);

      // Fallback to regex extraction if URL parsing fails
      // This handles both normal URL parameters and hash fragments
      const fullRegex = new RegExp(`[?&]${paramName}=([^&#]*)`, "i");
      const match = url.match(fullRegex);

      if (match && match[1]) {
        return decodeURIComponent(match[1]);
      }

      return null;
    }
  };

  /**
   * Copy URL parameter to clipboard
   * @param {string} url - Full URL string
   * @param {string} paramName - Parameter name to extract and copy
   * @param {Function} onSuccess - Optional callback for success
   * @param {Function} onError - Optional callback for error
   * @returns {Promise<boolean>} Success state
   */
  const copyUrlParam = async (url, paramName, onSuccess, onError) => {
    const paramValue = extractUrlParam(url, paramName);

    if (!paramValue) {
      const error = `No '${paramName}' parameter found in URL`;
      setCopyStatus({ copied: false, error });
      if (onError) onError(error);
      return false;
    }

    return copyToClipboard(paramValue, onSuccess, onError);
  };

  /**
   * Copy text to clipboard with multiple fallback methods
   * @param {string} text - Text to copy
   * @param {Function} onSuccess - Optional callback for success
   * @param {Function} onError - Optional callback for error
   * @returns {Promise<boolean>} Success state
   */
  const copyToClipboard = async (text, onSuccess, onError) => {
    setCopyStatus({ copied: false, error: null });

    try {
      // Method 1: Try the modern Clipboard API first
      await navigator.clipboard.writeText(text);
      setCopyStatus({ copied: true, error: null });
      if (onSuccess) onSuccess();
      return true;
    } catch (err) {
      console.warn("Clipboard API failed:", err.message);

      // Method 2: Try execCommand as fallback
      try {
        const textArea = document.createElement("textarea");
        textArea.value = text;

        // Make the textarea out of viewport
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        textArea.style.top = "-999999px";
        document.body.appendChild(textArea);

        textArea.focus();
        textArea.select();

        const successful = document.execCommand("copy");
        document.body.removeChild(textArea);

        if (successful) {
          setCopyStatus({ copied: true, error: null });
          if (onSuccess) onSuccess();
          return true;
        } else {
          throw new Error("execCommand('copy') failed");
        }
      } catch (fallbackErr) {
        console.error("Fallback clipboard method failed:", fallbackErr);
        const error = "Browser denied clipboard access";
        setCopyStatus({ copied: false, error });
        if (onError) onError(error);
        return false;
      }
    }
  };

  /**
   * Reset copy status
   */
  const resetCopyStatus = () => {
    setCopyStatus({ copied: false, error: null });
  };

  return {
    copyToClipboard,
    copyUrlParam,
    extractUrlParam,
    resetCopyStatus,
    copyStatus,
  };
};

export default useClipboard;
